import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import { Icon } from './index';
import supportService from '../../services/support.service';
import styles from '../../styles/components/SupportModal.module.css';

/**
 * Support Modal Component
 * Allows users to send support requests to admin
 */
function SupportModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const CATEGORIES = [
    { value: 'technical', label: t('supportModal.categoryTechnical') },
    { value: 'billing', label: t('supportModal.categoryBilling') },
    { value: 'general', label: t('supportModal.categoryGeneral') },
    { value: 'feedback', label: t('supportModal.categoryFeedback') },
  ];

  const resetForm = () => {
    setCategory('');
    setSubject('');
    setMessage('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!category) {
      setError(t('supportModal.errorNoCategory'));
      return;
    }
    if (!subject.trim()) {
      setError(t('supportModal.errorNoSubject'));
      return;
    }
    if (!message.trim()) {
      setError(t('supportModal.errorNoMessage'));
      return;
    }

    setLoading(true);

    try {
      await supportService.createTicket({
        category,
        subject: subject.trim(),
        message: message.trim()
      });

      setSuccess(true);
    } catch (err) {
      setError(err.message || t('supportModal.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  // Erfolgsansicht
  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title={t('supportModal.titleSuccess')}>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>
            <Icon name="check" size="xl" />
          </div>
          <h3 className={styles.successTitle}>{t('supportModal.successTitle')}</h3>
          <p className={styles.successText}>{t('supportModal.successText')}</p>
          <Button
            variant="primary"
            fullWidth
            onClick={handleClose}
          >
            {t('supportModal.close')}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('supportModal.titleDefault')}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <p className={styles.intro}>{t('supportModal.intro')}</p>

        <Input.Select
          label={t('supportModal.categoryLabel')}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={CATEGORIES}
          placeholder={t('supportModal.categoryPlaceholder')}
          required
        />

        <Input
          label={t('supportModal.subjectLabel')}
          placeholder={t('supportModal.subjectPlaceholder')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          required
        />

        <Input.Textarea
          label={t('supportModal.messageLabel')}
          placeholder={t('supportModal.messagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={5000}
          required
        />

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={loading}
        >
          {t('supportModal.submit')}
        </Button>
      </form>
    </Modal>
  );
}

export default SupportModal;
